import { BufferGeometry, Material, Scene } from "three";
import { RenderSceneReparentManager } from "./RenderSceneReparent";
import { logRenderSceneDebugBundle } from "./RenderSceneDebugLog";

/**
 * WebGL için logical sahne dışında `Scene` + karelik reparent; Engine sadece delegasyon yapar.
 */
export class RenderSceneSwapCoordinator {
  private readonly _sceneToRender: Scene;
  private readonly _reparent: RenderSceneReparentManager;
  private _enabled = false;
  private _debugLog = false;
  private _debugEachFrame = false;
  private _debugOnceAfterEnable = false;

  constructor() {
    this._sceneToRender = new Scene();
    this._reparent = new RenderSceneReparentManager(this._sceneToRender);
  }

  get sceneToRender(): Scene {
    return this._sceneToRender;
  }

  get enabled(): boolean {
    return this._enabled;
  }

  /**
   * Kapatılırken composer `RenderPass` sahnesini logical’a çeker.
   */
  setEnabled(
    value: boolean,
    logicalScene: Scene | undefined,
    setComposerScene: (scene: Scene) => void,
  ): void {
    this._enabled = value;
    if (!value && logicalScene) {
      setComposerScene(logicalScene);
    }
  }

  /**
   * `setUseRenderSceneSwap(true)` ile birlikte: aynı materyaldeki mesh’leri world uzayında birleştirip
   * materyal başına tek draw call (SkinnedMesh / InstancedMesh / uyumsuz geometri → eski reparent).
   */
  setMergeMeshesPerMaterial(value: boolean): void {
    this._reparent.setMergeMeshesPerMaterial(value);
  }

  get mergeMeshesPerMaterial(): boolean {
    return this._reparent.mergeMeshesPerMaterialEnabled;
  }

  setDebugLog(enabled: boolean, eachFrame: boolean): void {
    this._debugLog = enabled;
    this._debugEachFrame = eachFrame;
    if (enabled) this._debugOnceAfterEnable = true;
  }

  get debugLog(): boolean {
    return this._debugLog;
  }

  get debugEachFrame(): boolean {
    return this._debugEachFrame;
  }

  getWebGLScene(logical: Scene): Scene {
    return this._enabled ? this._sceneToRender : logical;
  }

  /**
   * Swap açıksa yapı + ortam senkronu; dönüş: bu karede grup yeniden kuruldu mu.
   */
  prepareFrame(
    logical: Scene,
    structureSignature: string,
    buckets: ReadonlyMap<Material, readonly BufferGeometry[]>,
  ): boolean {
    if (!this._enabled) return false;
    const rebuilt = this._reparent.syncStructure(
      logical,
      structureSignature,
      buckets,
    );
    this._reparent.syncEnvironmentFrom(logical);
    return rebuilt;
  }

  pushAndLog(
    renderStructureRebuilt: boolean,
    structureSignature: string,
    buckets: ReadonlyMap<Material, readonly BufferGeometry[]>,
    setComposerScene: (scene: Scene) => void,
  ): void {
    this._reparent.pushMeshesToRenderScene();
    if (this._debugLog) {
      const once = this._debugOnceAfterEnable;
      if (renderStructureRebuilt || this._debugEachFrame || once) {
        if (once) this._debugOnceAfterEnable = false;
        logRenderSceneDebugBundle({
          reason: renderStructureRebuilt
            ? "topoloji / bucket değişti"
            : this._debugEachFrame
              ? "her kare (logEachFrame)"
              : "debug açılışı (tek kare)",
          structureSignature,
          structureRebuilt: renderStructureRebuilt,
          sceneToRender: this._sceneToRender,
          buckets,
        });
      }
    }
    setComposerScene(this._sceneToRender);
  }

  popAndRestoreComposer(
    logical: Scene,
    setComposerScene: (scene: Scene) => void,
  ): void {
    this._reparent.popMeshesFromRenderScene();
    setComposerScene(logical);
  }

  /**
   * Konsoldan anlık döküm; swap kapalıysa sadece imza + bucket anlamlıdır.
   */
  logStateNow(
    logical: Scene | undefined,
    structureSignature: string,
    buckets: ReadonlyMap<Material, readonly BufferGeometry[]>,
    reason = "manual",
  ): void {
    if (logical && this._enabled) {
      this._reparent.syncStructure(logical, structureSignature, buckets);
      this._reparent.syncEnvironmentFrom(logical);
      this._reparent.pushMeshesToRenderScene();
      try {
        logRenderSceneDebugBundle({
          reason: `${reason} (swap açık, push sonrası — GPU hiyerarşisi)`,
          structureSignature,
          structureRebuilt: false,
          sceneToRender: this._sceneToRender,
          buckets,
        });
      } finally {
        this._reparent.popMeshesFromRenderScene();
      }
    } else {
      logRenderSceneDebugBundle({
        reason: `${reason} (swap kapalı — sadece imza + bucket’lar; render sahnesi boş olabilir)`,
        structureSignature,
        structureRebuilt: false,
        sceneToRender: this._sceneToRender,
        buckets,
      });
    }
  }
}
